import { type IconType, Text, ToggleButton } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import type { Control, FieldError, FieldPath, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";

import { FiltersToggleButtonGroup } from "../../ShiftDiscovery/Filters/BottomSheet/ToggleButtonGroup";

export interface FilterOption {
  label: string;
  value: string;
  icon?: IconType;
}

export interface FilterOptionGroup {
  label: string;
  options: FilterOption[];
}

interface FilterChipsProps<TFormValues extends FieldValues> {
  name: FieldPath<TFormValues>;
  control: Control<TFormValues>;
  options?: FilterOption[];
  optionGroups?: FilterOptionGroup[];
  title: string;
  /**
   * Delimiter used to separate multiple selected values in the underlying field value.
   * Defaults to a comma (,).
   * Pass a delimiter that does NOT appear inside any option values.
   */
  delimiter?: string;
  allOptions?: boolean;
  /** Show "Any" button for tag-based filters that support showing all options */
  showAnyOption?: boolean;
  /**
   * When true (default), the component behaves as a multi-select allowing any
   * number of chips to be selected. When false, it behaves as a single-select
   * (radio-style) where only one chip can be selected at a time.
   */
  multiSelect?: boolean;
  error?: FieldError;
  showTitle?: boolean;
}

export function FilterChips<TFormValues extends FieldValues>(props: FilterChipsProps<TFormValues>) {
  const {
    name,
    control,
    options,
    optionGroups,
    title,
    delimiter = ",",
    allOptions = true,
    showAnyOption = false,
    multiSelect = true,
    showTitle = true,
    error,
  } = props;

  const isGrouped = (optionGroups?.length ?? 0) > 0;
  const flatOptions = isGrouped ? optionGroups!.flatMap((group) => group.options) : (options ?? []);

  return (
    <Stack spacing={5}>
      {showTitle && (
        <Text semibold variant="body1">
          {title}
        </Text>
      )}
      <Stack spacing={3}>
        <Stack direction="row" gap={3} flexWrap="wrap">
          <Controller
            name={name}
            control={control}
            render={({ field }) => {
              const values = field.value as string | undefined;
              // Determine currently selected values based on multi-select mode
              const selectedValues: string[] = (() => {
                if (!values) {
                  return [];
                }

                if (multiSelect) {
                  return values.split(delimiter).filter(Boolean);
                }

                return [values];
              })();

              const allValues = flatOptions.map((option) => option.value);
              const isAllSelected =
                selectedValues.length === allValues.length &&
                selectedValues.every((value) => allValues.includes(value));
              const isAnySelected = selectedValues.length === 0;

              function handleToggleClick(value: string) {
                if (value === "All") {
                  if (!isAllSelected) {
                    field.onChange(allValues.join(delimiter));
                  }

                  return;
                }

                if (value === "Any") {
                  field.onChange("");
                  return;
                }

                if (!multiSelect) {
                  const isSelected = selectedValues.includes(value);

                  if (isSelected) {
                    return;
                  }

                  field.onChange(value);

                  return;
                }

                if (isAllSelected && selectedValues.includes(value)) {
                  field.onChange(value);
                  return;
                }

                const isSelected = selectedValues.includes(value);
                if (isSelected && selectedValues.length === 1) {
                  return;
                }

                const newValues = isSelected
                  ? selectedValues.filter((v) => v !== value)
                  : [...selectedValues, value];
                field.onChange(newValues.join(delimiter));
              }

              function renderChips(chipOptions: FilterOption[]) {
                return chipOptions.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <ToggleButton
                      key={option.value}
                      value={option.value}
                      selected={isSelected || isAllSelected}
                      iconType={option.icon}
                      onClick={() => {
                        handleToggleClick(option.value);
                      }}
                    >
                      {option.label}
                    </ToggleButton>
                  );
                });
              }

              if (optionGroups && optionGroups.length > 0) {
                const hasMultipleGroups = optionGroups.length > 1;

                return (
                  <Stack spacing={hasMultipleGroups ? 8 : 3}>
                    {optionGroups.map((group, index) => (
                      <Stack key={group.label} spacing={4}>
                        {group.label && (
                          <Text semibold variant="body1">
                            {group.label}
                          </Text>
                        )}
                        <FiltersToggleButtonGroup
                          value={multiSelect ? selectedValues : selectedValues[0]}
                          exclusive={!multiSelect}
                          aria-label={`${group.label || title} Toggle Buttons`}
                          onChange={() => {}}
                        >
                          {index === 0 && allOptions && showAnyOption && (
                            <ToggleButton
                              value="Any"
                              selected={isAnySelected}
                              onClick={() => {
                                handleToggleClick("Any");
                              }}
                            >
                              Any
                            </ToggleButton>
                          )}
                          {index === 0 && allOptions && (
                            <ToggleButton
                              value="All"
                              selected={isAllSelected}
                              onClick={() => {
                                handleToggleClick("All");
                              }}
                            >
                              All
                            </ToggleButton>
                          )}
                          {renderChips(group.options)}
                        </FiltersToggleButtonGroup>
                      </Stack>
                    ))}
                  </Stack>
                );
              }

              return (
                <FiltersToggleButtonGroup
                  value={multiSelect ? selectedValues : selectedValues[0]}
                  exclusive={!multiSelect}
                  aria-label={`${title} Filter Toggle Buttons`}
                  onChange={() => {
                    /* We handle state in individual buttons via onClick to retain existing behaviour */
                  }}
                >
                  {allOptions && showAnyOption && (
                    <ToggleButton
                      value="Any"
                      selected={isAnySelected}
                      onClick={() => {
                        handleToggleClick("Any");
                      }}
                    >
                      Any
                    </ToggleButton>
                  )}
                  {allOptions && (
                    <ToggleButton
                      value="All"
                      selected={isAllSelected}
                      onClick={() => {
                        handleToggleClick("All");
                      }}
                    >
                      All
                    </ToggleButton>
                  )}
                  {renderChips(flatOptions)}
                </FiltersToggleButtonGroup>
              );
            }}
          />
        </Stack>
        {error && (
          <Text variant="body2" color="error">
            {error.message}
          </Text>
        )}
      </Stack>
    </Stack>
  );
}
